import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import APP_CONFIG from '@/app.config';
import type { AdminUserFormValues, AdminUserSummary } from '../types/adminUsers';

type Mode = 'create' | 'edit';

type Props = {
  open: boolean;
  mode: Mode;
  user: AdminUserSummary | null;
  loading: boolean;
  onClose: () => void;
  onSubmit: (values: AdminUserFormValues) => Promise<void> | void;
};

type ValidationErrors = Partial<Record<keyof AdminUserFormValues, string>>;

const emptyValues: AdminUserFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  role: 'User',
  active: true,
  phone: '',
  internalNotes: '',
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function UserFormDialog({ open, mode, user, loading, onClose, onSubmit }: Props) {
  const [values, setValues] = useState<AdminUserFormValues>(emptyValues);
  const [errors, setErrors] = useState<ValidationErrors>({});

  useEffect(() => {
    if (!open) {
      return;
    }

    if (mode === 'edit' && user) {
      setValues({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role,
        active: user.active,
        phone: user.phone,
        internalNotes: user.internalNotes,
      });
    } else {
      setValues(emptyValues);
    }

    setErrors({});
  }, [mode, open, user]);

  const title = useMemo(
    () => (mode === 'create' ? 'Nouvel utilisateur' : `Modifier ${user?.fullName || 'l’utilisateur'}`),
    [mode, user?.fullName]
  );

  const handleChange = <K extends keyof AdminUserFormValues>(field: K, value: AdminUserFormValues[K]) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const validate = (): boolean => {
    const nextErrors: ValidationErrors = {};

    if (!values.firstName.trim()) {
      nextErrors.firstName = 'Le prénom est requis.';
    }

    if (!values.lastName.trim()) {
      nextErrors.lastName = 'Le nom est requis.';
    }

    if (!values.email.trim()) {
      nextErrors.email = 'L’email est requis.';
    } else if (!emailRegex.test(values.email.trim().toLowerCase())) {
      nextErrors.email = 'L’email n’est pas valide.';
    }

    if (!values.role) {
      nextErrors.role = 'Le rôle est requis.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    await onSubmit({
      ...values,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim().toLowerCase(),
      phone: values.phone.trim(),
      internalNotes: values.internalNotes.trim(),
    });
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Prénom"
                value={values.firstName}
                onChange={(event) => handleChange('firstName', event.target.value)}
                fullWidth
                required
                error={Boolean(errors.firstName)}
                helperText={errors.firstName}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Nom"
                value={values.lastName}
                onChange={(event) => handleChange('lastName', event.target.value)}
                fullWidth
                required
                error={Boolean(errors.lastName)}
                helperText={errors.lastName}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Email"
                value={values.email}
                onChange={(event) => handleChange('email', event.target.value)}
                fullWidth
                required
                disabled={mode === 'edit'}
                error={Boolean(errors.email)}
                helperText={errors.email || (mode === 'edit' ? 'L’email reste inchangé après création.' : 'Le compte Auth Firebase sera créé avec cet email.')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth error={Boolean(errors.role)}>
                <InputLabel id="admin-user-role-label">Rôle</InputLabel>
                <Select
                  labelId="admin-user-role-label"
                  label="Rôle"
                  value={values.role}
                  onChange={(event) => handleChange('role', event.target.value as AdminUserFormValues['role'])}
                >
                  {APP_CONFIG.userRoles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
                {errors.role ? <FormHelperText>{errors.role}</FormHelperText> : null}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Téléphone"
                value={values.phone}
                onChange={(event) => handleChange('phone', event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={values.active}
                    onChange={(event) => handleChange('active', event.target.checked)}
                  />
                }
                label={values.active ? 'Compte actif' : 'Compte inactif'}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Notes internes"
                value={values.internalNotes}
                onChange={(event) => handleChange('internalNotes', event.target.value)}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Annuler
        </Button>
        <Button onClick={handleSubmit} disabled={loading} variant="contained">
          {mode === 'create' ? 'Créer l’utilisateur' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}